import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Wallet, ArrowLeft, ArrowUpDown, Users, Trash2, UserX, Paperclip } from 'lucide-react';

const API_URL = '';

const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PettyCashAdminTab() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEntries, setUserEntries] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortAsc, setSortAsc] = useState(false);

  const loadBalances = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/petty-cash/admin/balances`);
      setBalances(res.data);
    } catch {
      toast.error('Failed to load petty cash balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  const viewEmployee = async (userId, fullName) => {
    setDetailLoading(true);
    setSelectedUser({ id: userId, name: fullName });
    try {
      const res = await axios.get(`${API_URL}/api/petty-cash/admin/${userId}`);
      setUserEntries(res.data);
    } catch {
      toast.error('Failed to load employee entries');
    } finally {
      setDetailLoading(false);
    }
  };

  const goBack = () => {
    setSelectedUser(null);
    setUserEntries(null);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/petty-cash/admin/entry/${entryId}`);
      toast.success('Entry deleted');
      if (selectedUser) viewEmployee(selectedUser.id, selectedUser.name);
      loadBalances();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteUserPettyCash = async (userId, userName) => {
    if (!window.confirm(`Delete ALL petty cash entries for ${userName}? This removes every record and receipt. This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/api/petty-cash/admin/user/${userId}`);
      toast.success(`Deleted ${res.data?.count || 0} entries`);
      goBack();
      loadBalances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user entries');
    }
  };

  // Detail view for a selected employee
  if (selectedUser) {
    const entries = userEntries?.entries || [];
    const sorted = [...entries].sort((a, b) => sortAsc ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date));

    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={goBack} className="gap-1 text-xs" data-testid="btn-back-to-list">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to List
        </Button>

        <Card className="glass-effect border-border/40">
          <CardHeader className="p-3 sm:p-4 pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary" />
                {selectedUser.name} — Petty Cash
              </CardTitle>
              <Button variant="destructive" size="sm" className="h-7 text-xs gap-1"
                onClick={() => handleDeleteUserPettyCash(selectedUser.id, selectedUser.name)}
                data-testid="btn-delete-user-petty-cash">
                <UserX className="w-3 h-3" /> Delete All Entries
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            {detailLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : (
              <>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="p-2 rounded-md border border-red-500/20 bg-red-500/5 text-center">
                    <p className="text-[10px] sm:text-xs font-semibold text-red-400 uppercase">Expenses</p>
                    <p className="text-sm sm:text-base font-bold">{fmt(userEntries?.total_expenses || 0)}</p>
                  </div>
                  <div className="p-2 rounded-md border border-green-500/20 bg-green-500/5 text-center">
                    <p className="text-[10px] sm:text-xs font-semibold text-green-400 uppercase">Deposits</p>
                    <p className="text-sm sm:text-base font-bold">{fmt(userEntries?.total_deposit || 0)}</p>
                  </div>
                  <div className="p-2 rounded-md border border-yellow-500/20 bg-yellow-500/5 text-center">
                    <p className="text-[10px] sm:text-xs font-semibold text-yellow-500 uppercase">Balance</p>
                    <p className={`text-sm sm:text-base font-bold ${(userEntries?.cash_on_hand || 0) < 0 ? 'text-red-500' : ''}`}>
                      {fmt(userEntries?.cash_on_hand || 0)}
                    </p>
                  </div>
                </div>

                {/* Sort toggle */}
                <div className="flex justify-end mb-2">
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setSortAsc(!sortAsc)} data-testid="btn-sort-detail">
                    <ArrowUpDown className="w-3 h-3" /> {sortAsc ? 'Oldest First' : 'Newest First'}
                  </Button>
                </div>

                {/* Entries table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="table-employee-entries">
                    <thead>
                      <tr className="border-b border-border/40 text-muted-foreground text-xs">
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                        <th className="text-left px-3 py-2 font-medium">Item</th>
                        <th className="text-left px-2 py-2 font-medium hidden sm:table-cell">Project</th>
                        <th className="text-right px-3 py-2 font-medium">Expenses</th>
                        <th className="text-right px-3 py-2 font-medium">Deposit</th>
                        <th className="text-center px-2 py-2 font-medium">Receipt</th>
                        <th className="text-center px-2 py-2 font-medium w-12">Del</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No entries</td></tr>
                      ) : sorted.map(entry => (
                        <tr key={entry.id} className="border-b border-border/20 hover:bg-muted/20 transition-colors" data-testid={`admin-row-${entry.id}`}>
                          <td className="px-3 py-2 text-xs whitespace-nowrap">{entry.date}</td>
                          <td className="px-3 py-2 text-sm">{entry.item}</td>
                          <td className="px-2 py-2 text-xs hidden sm:table-cell">
                            {entry.project_name ? <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-500 font-medium">{entry.project_name}</span> : <span className="text-muted-foreground/40">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-xs tabular-nums">
                            {entry.expenses > 0 ? <span className="text-red-400">{fmt(entry.expenses)}</span> : <span className="text-muted-foreground/40">-</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-xs tabular-nums">
                            {entry.deposit > 0 ? <span className="text-green-400">{fmt(entry.deposit)}</span> : <span className="text-muted-foreground/40">-</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            {entry.receipt_url ? (
                              <a href={`${API_URL}${entry.receipt_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary hover:underline text-xs gap-0.5">
                                <Paperclip className="w-3 h-3" /> View
                              </a>
                            ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              onClick={() => handleDeleteEntry(entry.id)}
                              data-testid={`btn-admin-delete-${entry.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main list: employees sorted by lowest balance first, zero balance hidden
  return (
    <Card className="glass-effect border-border/40" data-testid="petty-cash-admin-card">
      <CardHeader className="p-3 sm:p-4 pb-2">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Employee Petty Cash Balances
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Employees with lowest balance shown first. Zero balance employees are hidden.
        </p>
      </CardHeader>
      <CardContent className="p-0 sm:p-2">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : balances.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No employees with active petty cash entries</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-balances">
              <thead>
                <tr className="border-b border-border/40 text-muted-foreground text-xs">
                  <th className="text-left px-3 py-2 font-medium">Employee</th>
                  <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Department</th>
                  <th className="text-right px-3 py-2 font-medium">Expenses</th>
                  <th className="text-right px-3 py-2 font-medium">Deposits</th>
                  <th className="text-right px-3 py-2 font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {balances.map(b => (
                  <tr
                    key={b.user_id}
                    className="border-b border-border/20 hover:bg-muted/20 cursor-pointer transition-colors"
                    onClick={() => viewEmployee(b.user_id, b.full_name)}
                    data-testid={`row-employee-${b.user_id}`}
                  >
                    <td className="px-3 py-2.5">
                      <span className="text-sm font-medium text-foreground">{b.full_name}</span>
                      <span className="text-xs text-muted-foreground sm:hidden ml-1">({b.department})</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground hidden sm:table-cell">{b.department}</td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-red-400">{fmt(b.total_expenses)}</td>
                    <td className="px-3 py-2.5 text-right text-xs tabular-nums text-green-400">{fmt(b.total_deposit)}</td>
                    <td className="px-3 py-2.5 text-right text-sm tabular-nums font-semibold">
                      <span className={b.cash_on_hand < 0 ? 'text-red-500' : 'text-foreground'}>{fmt(b.cash_on_hand)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
